import React, { useMemo, useState } from "react";
import {
  CHECK_SELECTED_USER_ID,
  formatToTwoDecimalPlaces,
  isEmpty,
} from "helper/Functions";
import UserService from "service/UserService";
import SectionLoader from "components/ui/loaders/SectionLoader";
import RoleBasedDropdowns from "../FilterFormComponents/RoleBasedDropdown";
import { API_URL } from "helper/Constants";
import { ReactComponent as IconSearch } from "assets/icons/icon-search.svg";
import { ReactComponent as IconTransfer } from "assets/icons/icon-action-transfer.svg";
import { ReactComponent as IconReport } from "assets/icons/icon-action-report.svg";
import { ReactComponent as IconChangePassword } from "assets/icons/icon-action-change-password.svg";
import CustomTable from "components/ui/tables/CustomTable";
import moment from "moment";
import Auth from "service/AuthService";
import { toast } from "react-toastify";
import Modal from "components/ui/Modal";
import ManageUserChangePassword from "./ManageUserChangePassword";
import { useAuth } from "provider/AuthContext";
import { useTranslation } from "react-i18next";
import ChangePasswordPage from "pages/auth/ChangePasswordPage";

const ManageUser = () => {
  const userService = new UserService(API_URL);
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [responseMsg, setReponseMsg] = useState("");
  const [allUsersBalance, setAllUsersBalance] = useState([]);
  const [sortCriteria, setSortCriteria] = useState("alphabetically");
  const [baseRole, setBaseRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState({});
  const [selectedInputValues, setSelectedInputValues] = useState({});

  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [
    openCurrentUserChangePasswordModal,
    setOpenCurrentUserChangePasswordModal,
  ] = useState(false);

  const [userIdForPasswordUpdate, setUserIdForPasswordUpdate] = useState("");

  const fetchUserBalance = async () => {
    setLoading(true);
    try {
      const dto = {
        type:
          CHECK_SELECTED_USER_ID(selectedInputValues).key || selectedRole.role,
        userId: CHECK_SELECTED_USER_ID(selectedInputValues).value,
      };
      let data = await userService.getUserBalance(dto);
      setAllUsersBalance(data);
      // setReponseMsg(response.message);
      //console.log("API response:", data);
      setLoading(false);
    } catch (error) {
      setReponseMsg(error);
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const sortedUsers = [...allUsersBalance]?.sort((a, b) => {
    if (sortCriteria === "alphabetically") {
      return a.name.localeCompare(b.name);
    }
    if (sortCriteria === "balance") {
      return b.balance - a.balance;
    }
    return 0;
  });

  const handleSearch = () => {
    fetchUserBalance();
  };

  const handleReset = () => {
    setBaseRole(null);
    setSelectedRole(null);
    setAllUsersBalance([]);
    setSelectedInputValues({});
  };

  const tableData = sortedUsers?.map((item, index) => {
    return {
      ...item,
    };
  });

  const totalUsers = sortedUsers.length;
  const totalBal = sortedUsers.reduce((acc, row) => acc + row.balance, 0);
  const totalNetworkBal = sortedUsers.reduce(
    (acc, row) => acc + row.networkBalance,
    0
  );

  const tableColumns = useMemo(
    () => [
      {
        accessorKey: "createdBy",
        header: "User",
        size: 150,
        Cell: ({ cell }) => (
          <div className="flex items-center gap-2">
            {cell.row.original.createdBy && (
              <span className="flex items-center gap-2">
                {cell.row.original.createdBy}
                <span className="text-gray-700 -mt-0.5 text-2xl leading-3 font-mono font-lighter">
                  →
                </span>
              </span>
            )}
            {cell.row.original?.name}
          </div>
        ),
        Footer: () => <>Total ({totalUsers} players)</>,
      },
      {
        accessorKey: "role",
        header: "Role",
        size: 80,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.role
              ? cell.row.original.role
              : selectedRole?.role}
          </>
        ),
      },

      {
        accessorKey: "balance",
        header: "Balance",
        size: 50,
        Cell: ({ cell }) => (
          <div className="flex">
            <>
              {formatToTwoDecimalPlaces(
                cell.row.original.balance ? cell.row.original.balance : 0
              )}{" "}
              {user?.currency}
            </>
          </div>
        ),
        Footer: () => (
          <>
            {formatToTwoDecimalPlaces(totalBal)} {user?.currency}
          </>
        ),
      },
      {
        accessorKey: "networkBalance",
        header: "Network Balance",
        size: 50,
        Cell: ({ cell }) => (
          <>
            {formatToTwoDecimalPlaces(
              cell.row.original?.networkBalance
                ? cell.row.original?.networkBalance
                : 0
            )}{" "}
            {user?.currency}
          </>
        ),
        Footer: () => (
          <>
            {formatToTwoDecimalPlaces(totalNetworkBal)} {user?.currency}
          </>
        ),
      },
      {
        accessorKey: "#",
        header: "Actions",
        size: 50,
        Cell: ({ cell }) => (
          <div className="flex space-x-1">
            <span className="cursor-pointer text-nowrap flex flex-1 justify-center items-center gap-1 hover:bg-[#f2c41a] text-md text-[#242424] focus:outline-none rounded-lg bg-white border border-black mx-1 max-w-40 w-full  py-1">
              <IconTransfer />
              <span>Transfer</span>
            </span>
            <span className="cursor-pointer text-nowrap flex flex-1 justify-center items-center gap-1 hover:bg-[#f2c41a] focus:outline-none rounded-lg bg-white border border-black mx-1 max-w-40 w-full  py-1">
              <IconReport />
              <span>Report</span>
            </span>
            <span
              className="cursor-pointer text-nowrap flex flex-1 justify-center items-center gap-1 hover:bg-[#f2c41a] focus:outline-none rounded-lg bg-white border border-black mx-1 max-w-60 w-full py-1"
              onClick={() => {
                if (user._id !== cell.row.original.creatorId._id) {
                  toast.error(
                    "Only creator can have access to change password"
                  );
                  return;
                }
                setOpenChangePasswordModal(true);
                setUserIdForPasswordUpdate(cell.row.original.id);
              }}
            >
              {" "}
              <IconChangePassword />
              <span>{t("Change Password")}</span>
            </span>
          </div>
        ),
      },
    ],
    [selectedRole?.role, totalBal, totalNetworkBal, totalUsers]
  );

  return (
    <div className="flex flex-col w-full">
      <header className="rounded-lg bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">
          Manage Users
        </h1>
      </header>

      <div className="flex-1 overflow-auto pt-5">
        <div className="w-full lg:flex  rounded-lg">
          <>
            <RoleBasedDropdowns
              showSelectRole
              baseRole={baseRole}
              setBaseRole={setBaseRole}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
              selectedInputValues={selectedInputValues}
              setSelectedInputValues={setSelectedInputValues}
            />
          </>
          {/* Action Buttons */}
        </div>
        <div className="md:flex items-center justify-between mt-7">
          <div className="lg:flex gap-3 w-full lg:max-w-sm">
            <button
              className={`bg-primary hover:bg-primary/80 text-black w-full md:max-w-36 font-bold py-2 rounded-lg ${
                !isEmpty(selectedRole)
                  ? " text-black cursor-pointer"
                  : "bg-[#f2c41a] text-black cursor-not-allowed opacity-50"
              }`}
              type="button"
              onClick={handleSearch}
              disabled={isEmpty(selectedRole)} // Disable button if no value is selected
            >
              SEARCH
            </button>
            <button
              className={`text-center border border-black mt-4 sm:mt-0 md:max-w-36 rounded-lg text-md hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 w-full`}
              type="reset"
              onClick={handleReset}
            >
              RESET
            </button>
          </div>
          <button
            type="button"
            className={`flex justify-center items-center gap-1 mt-4 sm:mt-0 md:max-w-56 text-primary border border-primary [&_svg_path]:fill-primary [&_svg]:w-6 [&_svg]:h-6 rounded-lg text-md hover:bg-gray-300 text-gray-700 font-bold py-2 px-3 w-full`}
            onClick={() => setOpenCurrentUserChangePasswordModal(true)}
          >
            <IconChangePassword />
            <span>{t("Change Password")}</span>
          </button>
        </div>
        <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />
        {!loading ? (
          <>
            {/* Display the user list table if there are filtered users */}
            {allUsersBalance && allUsersBalance.length ? (
              <>
                {baseRole && (
                  <div className="mb-4">
                    <span className="flex text-sm text-black">Sort by:</span>
                    <label htmlFor="sortByUser" className="space-x-2 pr-3 ml-2">
                      <input
                        type="radio"
                        name="sortCriteria"
                        id="sortByUser"
                        value="alphabetically"
                        checked={sortCriteria === "alphabetically"}
                        onChange={(e) => setSortCriteria(e.target.value)}
                        // className="focus:ring-2 peer focus:ring-blue-500"
                      />
                      <span className="text-black font-bold">
                        Alphabetically
                      </span>
                    </label>
                    <label htmlFor="sortByBalance" className="space-x-2">
                      <input
                        type="radio"
                        name="sortCriteria"
                        id="sortByBalance"
                        value="balance"
                        checked={sortCriteria === "balance"}
                        onChange={(e) => setSortCriteria(e.target.value)}
                      />
                      <span className="text-black font-bold">Balance</span>
                    </label>
                  </div>
                )}
                <CustomTable
                  enableStickyFooter
                  columns={tableColumns}
                  data={tableData ? tableData : []}
                  enableGlobalFilter={true}
                  enableFilterMatchHighlighting={true}
                />
                {openChangePasswordModal && (
                  <ManageUserChangePassword
                    isOpen={openChangePasswordModal}
                    onClose={() => setOpenChangePasswordModal(false)}
                    userIdForPasswordUpdate={userIdForPasswordUpdate}
                    setUserIdForPasswordUpdate={setUserIdForPasswordUpdate}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
                <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
                  <IconSearch />
                </div>
                <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
                  {responseMsg
                    ? responseMsg
                    : "Search all available records by selecting one of the filters above!"}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="flex justify-center py-8">
            <SectionLoader bgColor="light" />
          </div>
        )}
      </div>
      {openCurrentUserChangePasswordModal && (
        <ChangePasswordPage
          isOpen={openCurrentUserChangePasswordModal}
          onClose={() => setOpenCurrentUserChangePasswordModal(false)}
        />
      )}
    </div>
  );
};

export default ManageUser;
